import { useCallback, useRef, useState } from 'preact/hooks';
import './ImagePicker.less';
import { DragImagePlaceholder } from './DragImagePlaceholder';
import type { ChangeEvent } from 'preact/compat';
import { useTranslation } from '../../hooks/useTranslation';

export type ImagePickerProps = {
    onChange: (imageBase64: string) => void;
};

const ImagePicker = ({ onChange }: ImagePickerProps) => {
    const { t } = useTranslation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isDragOver, setDragOver] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const processFile = useCallback(
        (file: File) => {
            if (!file.type.startsWith('image/')) {
                setErrorMessage(t('PRODUCT_SEARCH_BY_IMAGE_INVALID_IMAGE'));
                return;
            }

            const reader = new FileReader();
            reader.onload = e => {
                if (typeof e.target?.result === 'string') {
                    setErrorMessage('');
                    onChange(e.target.result);
                }
            };
            reader.onerror = () => {
                setErrorMessage(
                    `${t('PRODUCT_SEARCH_BY_IMAGE_UPLOAD_ERROR')}. ${t('PRODUCT_SEARCH_BY_IMAGE_UPLOAD_ERROR_SUBTITLE')}`
                );
            };
            reader.readAsDataURL(file);
        },
        [onChange]
    );

    const onDropFile = useCallback(
        (event: DragEvent) => {
            event.preventDefault();
            setDragOver(false);
            if (event.dataTransfer?.files?.length) {
                const file = event.dataTransfer.files[0];
                processFile(file);
            }
        },
        [processFile]
    );

    const onSelectFile = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const currentTarget = event.currentTarget;
            if (currentTarget.files?.length) {
                const file = currentTarget.files[0];
                processFile(file);
            }
        },
        [processFile]
    );
    return (
        <div
            className={`bri-ImagePicker`}
            onDragLeave={event => {
                event.preventDefault();
                setDragOver(false);
            }}
            onDragOver={event => {
                event.preventDefault();
                setDragOver(true);
            }}
            onDrop={onDropFile}
        >
            {!isDragOver && (
                <div
                    className="bri-ImagePicker-empty-placeholder"
                    onClick={() => {
                        fileInputRef.current?.click();
                    }}
                >
                    <DragImagePlaceholder />
                    <div className="bri-ImagePicker-text-1 bri-DisplayFont">
                        {t(
                            'PRODUCT_SEARCH_BY_IMAGE_DRAG_AND_DROP_PLACEHOLDER_1'
                        )}
                    </div>
                    <div className="bri-ImagePicker-text-2 bri-DisplayFont">
                        {t(
                            'PRODUCT_SEARCH_BY_IMAGE_DRAG_AND_DROP_PLACEHOLDER_2'
                        )}
                    </div>
                </div>
            )}
            <div
                className={`bri-ImagePicker-error bri-BodySmallFont ${
                    errorMessage === '' ? 'bri-hidden' : ''
                }`}
            >
                {errorMessage}
            </div>
            {isDragOver && (
                <div className="bri-ImagePicker-drag-over-placeholder">
                    <div className="bri-ImagePicker-text-1 bri-DisplayFont">
                        {t('PRODUCT_SEARCH_BY_IMAGE_DRAG_AND_DROP_HOVER')}
                    </div>
                </div>
            )}

            <input
                className={`bri-ImagePicker-file`}
                type="file"
                ref={fileInputRef}
                onChange={onSelectFile}
                accept="image/*"
                multiple={false}
            />
        </div>
    );
};

export default ImagePicker;
